import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ActivitySeatBookingContainerStyled } from "./styled";
import { userService } from "apiServices";
import { ROUTE_PATH } from "utils/constants/routePath";
import { AlertControl } from "components/Modals/Alert";
import { Buttons, Displays, Headers, Navigations } from "components";
import {
  SEAT_BOOKING_CLOSED_TEXT,
  SEAT_BOOKING_ERROR_CODE,
  SEAT_BOOKING_ERROR_KEY,
} from "utils/functions/seatBooking";
import SeatSelectStep from "./Steps/SeatSelectStep";
import BookerInfoStep from "./Steps/BookerInfoStep";
import PaymentStep from "./Steps/PaymentStep";
import ReviewStep from "./Steps/ReviewStep";
import DoneStep from "./Steps/DoneStep";

const EXPIRED_CODES = ["DRAFT_EXPIRED", "NOT_DRAFT"];
const MAP_REFRESH_MS = 15000;

// ขั้นตอนจองที่นั่ง S2–S6 ขั้นอยู่ใน ?step= (กด back ของ browser ได้) ส่วน draft อยู่ที่ server
// รีเฟรชหน้าแล้วกลับมาขั้นที่ทำได้จริงตามข้อมูล draft (สลิปยังไม่ส่งเก็บแค่ในหน้า ต้องแนบใหม่)
const ActivitySeatBookingContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(["seat", "common"]);
  const authenRedux = useSelector((state) => state.authenRedux);

  const [_isLoading, _setIsLoading] = useState(true);
  const [_isBgLoading, _setIsBgLoading] = useState(false);
  const [_info, _setInfo] = useState(null);
  const [_errorKey, _setErrorKey] = useState(null);
  const [_map, _setMap] = useState(null);
  const [_draft, _setDraft] = useState(null);
  // ไฟล์สลิปที่ server ตรวจผ่านแล้ว (ใช้แสดงชื่อไฟล์ในหน้าตรวจสอบ)
  const [_slipFile, _setSlipFile] = useState(null);
  // ชื่อผู้ชมกรอกในหน้าเลือกที่นั่ง (S2) แต่บันทึกขึ้น server พร้อมข้อมูลผู้จอง (S3)
  const [_viewerNames, _setViewerNames] = useState({});
  // countdown ใช้เวลา server (เวลาเครื่องลูกค้าอาจเพี้ยน)
  const [_serverOffset, _setServerOffset] = useState(0);
  const [_now, _setNow] = useState(Date.now());
  const _expiredHandledRef = useRef(false);

  const stepParam = searchParams.get("step") || "seat";
  let step = "seat";
  if (stepParam === "done") step = "done";
  else if (!_draft || stepParam === "seat") step = "seat";
  else if (!_draft.code || stepParam === "info") step = "info";
  else if (stepParam === "review" && (_slipFile || _draft.has_pending_slip)) step = "review";
  else step = "payment";

  const _goStep = (nextStep, extra = {}, replace = false) =>
    setSearchParams({ step: nextStep, ...extra }, { replace });
  const _backToActivity = useCallback(
    () => navigate(`${ROUTE_PATH.ACTIVITIES}/${id}`, { replace: true }),
    [navigate, id]
  );

  // URL ต้องตรงกับขั้นที่แสดงจริง (เช่น กด back หลังส่งแล้ว ไม่มี draft แล้วจึงกลับไปหน้าเลือกที่นั่ง)
  useEffect(() => {
    if (_isLoading || step === stepParam) return;
    setSearchParams(step === "seat" ? {} : { step }, { replace: true });
  }, [_isLoading, step, stepParam, setSearchParams]);

  const _fetchMap = useCallback(async () => {
    const res = await userService.GET_SEAT_BOOKING_MAP(id);
    if (res && res.status === 200) _setMap(res.data);
  }, [id]);

  const _fetchInfo = useCallback(async () => {
    const res = await userService.GET_SEAT_BOOKING_INFO(id);
    if (res && res.status === 200) {
      _setInfo(res.data);
      _setDraft(res.data.my_draft);
      if (res.data.my_draft) {
        _setViewerNames(
          Object.fromEntries(res.data.my_draft.seats.map((s) => [s.seat_id, s.viewer_name || ""]))
        );
      }
      _setServerOffset(new Date(res.data.server_now).getTime() - Date.now());
      _setErrorKey(null);
    } else {
      _setErrorKey(SEAT_BOOKING_ERROR_KEY(res, "This activity is not available for seat booking"));
    }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([_fetchInfo(), _fetchMap()]);
      _setIsLoading(false);
    };
    fetchData();
  }, [_fetchInfo, _fetchMap]);

  // ผังที่นั่งอัปเดตเองระหว่างเลือก (คนอื่นจองไปแล้วจะเห็นเร็วขึ้น)
  useEffect(() => {
    if (step !== "seat") return undefined;
    const timer = setInterval(_fetchMap, MAP_REFRESH_MS);
    return () => clearInterval(timer);
  }, [step, _fetchMap]);

  useEffect(() => {
    const timer = setInterval(() => _setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // เปลี่ยนขั้นแล้วเริ่มดูจากบนสุด (ไม่ค้างตำแหน่ง scroll ของขั้นก่อน)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const remainingMs =
    _draft && _draft.expires_at ? new Date(_draft.expires_at).getTime() - (_now + _serverOffset) : null;

  const _handleExpired = useCallback(() => {
    if (_expiredHandledRef.current) return;
    _expiredHandledRef.current = true;
    _setDraft(null);
    _setSlipFile(null);
    AlertControl.show({
      title: t("Booking time expired", { ns: "seat" }),
      description: t("Your booking time has expired. The seats have been released.", { ns: "seat" }),
      btnLabel1: t("OK", { ns: "seat" }),
      onEvent1: _backToActivity,
    });
  }, [t, _backToActivity]);

  useEffect(() => {
    if (remainingMs !== null && remainingMs <= 0 && step !== "done") _handleExpired();
  }, [remainingMs, step, _handleExpired]);

  const _handleError = (res, fallback) => {
    if (EXPIRED_CODES.includes(SEAT_BOOKING_ERROR_CODE(res))) return _handleExpired();
    toast.error(t(SEAT_BOOKING_ERROR_KEY(res, fallback), { ns: "seat" }));
  };

  // คืนรายการที่นั่งที่ชน ให้หน้าเลือกที่นั่งเอาออกจากที่เลือก
  const _handleSeatNext = async (seatIds, viewerNames) => {
    _setViewerNames(viewerNames);
    _setIsBgLoading(true);
    const res = _draft
      ? await userService.PUT_SEAT_BOOKING_DRAFT_SEATS(_draft.booking_id, { seat_ids: seatIds })
      : await userService.POST_SEAT_BOOKING_DRAFT(id, { seat_ids: seatIds });
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      _expiredHandledRef.current = false;
      _setDraft(res.data);
      _setServerOffset(new Date(res.data.server_now).getTime() - Date.now());
      _goStep("info");
      return null;
    }
    const code = SEAT_BOOKING_ERROR_CODE(res);
    if (code === "SEAT_UNAVAILABLE") {
      toast.error(t(SEAT_BOOKING_ERROR_KEY(res), { ns: "seat" }));
      _fetchMap();
      return (res.data.error.items || []).map(String);
    }
    if (code === "DRAFT_EXISTS") {
      // เปิดจองไว้อีกแท็บ/อีกเครื่อง ดึง draft นั้นมาทำต่อ
      toast.error(t(SEAT_BOOKING_ERROR_KEY(res), { ns: "seat" }));
      await _fetchInfo();
      return null;
    }
    _handleError(res);
    return null;
  };

  const _handleInfoNext = async (params) => {
    _setIsBgLoading(true);
    const res = await userService.PUT_SEAT_BOOKING_DRAFT_DETAILS(_draft.booking_id, params);
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      _setDraft(res.data);
      _goStep("payment");
    } else {
      _handleError(res);
    }
  };

  // ตรวจสลิปทันทีที่เลือกไฟล์: สลิปซ้ำ/ไม่ใช่สลิป server ไม่ให้ผ่าน คืนข้อความ error ให้แสดงใต้ช่องอัปโหลด
  const _handleSlipCheck = async (file) => {
    const form = new FormData();
    form.append("image", file);
    _setIsBgLoading(true);
    const res = await userService.POST_SEAT_BOOKING_SLIP_CHECK(_draft.booking_id, form);
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      _setSlipFile(file);
      _setDraft((prev) => ({ ...prev, has_pending_slip: true }));
      return null;
    }
    if (EXPIRED_CODES.includes(SEAT_BOOKING_ERROR_CODE(res))) {
      _handleExpired();
      return null;
    }
    return SEAT_BOOKING_ERROR_KEY(res);
  };

  const _handlePaymentNext = () => _goStep("review");

  // สลิปตรวจและเก็บไว้กับ draft แล้วตั้งแต่หน้าชำระเงิน ส่งแค่คำสั่งยืนยัน
  const _handleSubmit = async () => {
    const form = new FormData();
    _setIsBgLoading(true);
    const res = await userService.POST_SEAT_BOOKING_SUBMIT(_draft.booking_id, form);
    _setIsBgLoading(false);
    if (res && res.status === 200) {
      // แทนที่ history เพื่อกด back แล้วไม่กลับไปหน้าส่งซ้ำ (ไปขั้น done ก่อนล้าง draft ให้ URL ไม่ถูกปรับเป็นหน้าเลือกที่นั่ง)
      _goStep("done", { code: res.data.code }, true);
      _setDraft(null);
      _setSlipFile(null);
    } else {
      _handleError(res);
    }
  };

  const minutes = remainingMs !== null ? Math.max(0, Math.floor(remainingMs / 60000)) : 0;
  const seconds = remainingMs !== null ? Math.max(0, Math.floor((remainingMs % 60000) / 1000)) : 0;
  const countdownText = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

  // ตาม design แถบนับถอยหลังอยู่หน้าข้อมูลผู้จอง (หน้าชำระเงินแสดงเวลาในข้อความใต้ QR) ลูกค้ายกเลิกเองไม่ได้ ปล่อยให้หมดเวลา
  const renderLockBanner = () =>
    _draft && step === "info" ? (
      <div className="lock_banner">
        {t("Seats {{seats}} are booked", {
          ns: "seat",
          seats: _draft.seats.map((s) => s.seat_id).join(", "),
        })}
        <br />
        {t("Please pay within", { ns: "seat" })} {countdownText} {t("min", { ns: "seat" })}
      </div>
    ) : null;

  const renderBody = () => {
    if (step === "done") {
      return <DoneStep code={searchParams.get("code")} onBack={_backToActivity} />;
    }
    if (_errorKey || !_info || !_map) {
      return (
        <div className="closed_block">
          <div>{t(_errorKey || "Something went wrong. Please try again.", { ns: "seat" })}</div>
          <Buttons.BgStandard theme_white_purple label={t("Back", { ns: "seat" })} onClick={_backToActivity} />
        </div>
      );
    }
    if (!_draft && !_info.can_book) {
      return (
        <div className="closed_block">
          <div>
            {SEAT_BOOKING_CLOSED_TEXT(t, _info)}
          </div>
          <Buttons.BgStandard theme_white_purple label={t("Back", { ns: "seat" })} onClick={_backToActivity} />
        </div>
      );
    }
    switch (step) {
      case "info":
        return (
          <BookerInfoStep
            draft={_draft}
            info={_info}
            memberInfo={authenRedux}
            viewerNames={_viewerNames}
            onBack={() => _goStep("seat")}
            onNext={_handleInfoNext}
          />
        );
      case "payment":
        return (
          <PaymentStep
            draft={_draft}
            info={_info}
            countdownText={countdownText}
            initialFile={_slipFile}
            onBack={() => _goStep("info")}
            onCheckSlip={_handleSlipCheck}
            onNext={_handlePaymentNext}
          />
        );
      case "review":
        return (
          <ReviewStep
            draft={_draft}
            slipFile={_slipFile}
            onBack={() => _goStep("payment")}
            onEdit={() => _goStep("info")}
            onSubmit={_handleSubmit}
          />
        );
      default:
        return (
          <SeatSelectStep
            info={_info}
            map={_map}
            draft={_draft}
            viewerNames={_viewerNames}
            onBack={_backToActivity}
            onNext={_handleSeatNext}
          />
        );
    }
  };

  return (
    <ActivitySeatBookingContainerStyled>
      <Displays.BGLoading visible={_isBgLoading} />
      <div className="breadcrumb_layout">
        <Navigations.Breadcrumb
          data={[
            { name: t("Home", { ns: "common" }), path: ROUTE_PATH.HOME },
            { name: t("Activity News", { ns: "common" }), path: ROUTE_PATH.ACTIVITIES },
            { name: t("Booking Seat", { ns: "seat" }) },
          ]}
        />
      </div>
      <div className="seat_booking_container">
        {/* ตาม design หน้าตรวจสอบ (S5) และส่งแล้ว (S6) ไม่มีหัว ACTIVITY */}
        {!["review", "done"].includes(step) && (
          <Headers.NewPageHeader title={"ACTIVITY : Booking Seat"} sub_title={t("Activity: Seat booking", { ns: "seat" })} />
        )}
        {_isLoading ? (
          <Displays.Loading />
        ) : (
          <>
            {renderLockBanner()}
            {renderBody()}
          </>
        )}
      </div>
    </ActivitySeatBookingContainerStyled>
  );
};

export default ActivitySeatBookingContainer;
